import React, { Fragment, useState, Suspense, useEffect, useRef } from 'react';
import { Formik, FormikProps } from 'formik';
import { ProjectConfig, UpdateModuleInput, UpdateModulesPayload, UpdateModuleError } from '@bechtel/types';
import {
  Box,
  Text,
  Spinner,
  SpinnerSize,
  useToastsDispatch,
  ToastAction,
  ToastType,
  ButtonAppearance,
  Heading,
} from '@bechtel/gds';
import { ErrorBoundary, SuspenseLoader, useAppUser, useProjectModulesQuery, ErrorRetry } from '@bechtel/client-common';
import { UPDATE_PROJECT_MODULES } from '@bechtel/client-gql';
import { get, filter, map, find } from 'lodash';
import { ApolloError, NetworkStatus, useMutation } from '@apollo/client';
import {
  ProjectAdminContainerStyled,
  ProjectAdminSubContainerStyled,
  ProjectAdminHeaderStyled,
  FooterStyled,
  FooterButtonStyled,
} from '../styled';
import { AppModuleExt, FormikState } from './types';
import { ModuleAdminItem } from './item';
import { useLingui } from '@lingui/react';
import UpdateProjectModulesError from './error';
import DeactivateConfirmModal from './deactivate-confirm';
import { t, Trans } from '@lingui/macro';
import { useModuleExtended } from './hooks';

const formikInitialValues: FormikState = {
  modules: [],
};

export const ModuleAdmin = () => {
  const { user } = useAppUser();
  const { i18n } = useLingui();
  const [initialValues, setInitialValues] = useState<FormikState>(formikInitialValues);
  const [updateModuleError, setUpdateModuleError] = useState<UpdateModuleError | ApolloError | null>(null);
  const [showDeactivateConfirmModal, setShowDeactivateConfirmModal] = useState(false);
  const modifiedModules = useRef<AppModuleExt[]>([]);
  const dispatchToast = useToastsDispatch();
  const moduleUtil = useModuleExtended();
  const moduleExtendedList = moduleUtil.getModuleExtendedList();
  const [
    getModules,
    {
      loading: moduleLoading,
      error: moduleError,
      data: moduleData,
      refetch: moduleRefetch,
      networkStatus: moduleNetworkStatus,
    },
  ] = useProjectModulesQuery();

  const projectConfig: ProjectConfig = get(moduleData, 'projectConfigById', undefined);
  const modules = projectConfig ? projectConfig.modules : [];
  const moduleExtList = initialValues.modules;

  useEffect(() => {
    if (!user || !user.project || !user.project.id) return;
    getModules({ variables: { id: user?.project?.id } });
  }, [getModules, user]);

  useEffect(() => {
    if (!projectConfig || !projectConfig.modules) return;
    const filteredModuleExtList = filter(moduleExtendedList, (item) => item.showModule);
    const moduleExtList: AppModuleExt[] = map(filteredModuleExtList, (item) => {
      const appModuleExtItem = find(modules, (moduleExtItem) => {
        return moduleExtItem.moduleTypeId === item.moduleTypeId;
      });
      return {
        ...item,
        ...appModuleExtItem,
        moduleDescription: i18n._(item.moduleDescription),
      };
    });
    setInitialValues({ modules: moduleExtList });
  }, [projectConfig, i18n]);

  const ModuleUpdateToast = () => {
    return (
      <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
        <Text family="graphik" weight="600" size="500">
          <Trans>Module data saved successfully.</Trans>
        </Text>
      </Box>
    );
  };

  const onUpdateModuleComplete = (data: { updateModules: UpdateModulesPayload }) => {
    const updateModuleError = get(data, 'updateModules.error', undefined);
    if (updateModuleError) {
      setUpdateModuleError(updateModuleError);
    } else {
      const toastAction: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.INFO,
          content: ModuleUpdateToast,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(toastAction);
    }
  };

  const [updateModules, { loading: updateProjectLoading }] = useMutation<
    { updateModules: UpdateModulesPayload },
    { input: UpdateModuleInput[] }
  >(UPDATE_PROJECT_MODULES, {
    onCompleted: onUpdateModuleComplete,
    onError: (error) => {
      setUpdateModuleError(error);
    },
  });

  const saveModules = (modifiedItems: AppModuleExt[]) => {
    updateModules({
      variables: {
        input: modifiedItems.map(({ id, isActive }) => ({
          id,
          isActive: isActive ? isActive : false,
        })),
      },
    });
  };

  return (
    <Fragment>
      <ProjectAdminContainerStyled>
        <ProjectAdminHeaderStyled>
          <Text family="graphik" size="600" weight="600" color="white">
            <Trans>Modules</Trans>
          </Text>
        </ProjectAdminHeaderStyled>

        {(moduleLoading || moduleNetworkStatus === NetworkStatus.refetch) && (
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
            data-testid="module-loading"
          >
            <Spinner size={SpinnerSize.LARGE} />
          </Box>
        )}
        {!moduleLoading && moduleError && (
          <div data-testid="module-error-retry">
            <ErrorRetry refetch={moduleRefetch}>
              <Trans>There was a problem loading module data. Please click the button below to try again.</Trans>
            </ErrorRetry>
          </div>
        )}
        {!moduleLoading && !moduleError && modules.length === 0 && (
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
            data-testid="module-no-data"
          >
            <Trans>No Module detail found.</Trans>
          </Box>
        )}

        {modules.length > 0 && !moduleLoading && !moduleError && (
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={(values) => {
              const modifiedItems = values.modules.filter((moduleItem) => {
                return initialValues.modules.some((defaultItem) => {
                  return defaultItem.id === moduleItem.id && defaultItem.isActive !== moduleItem.isActive;
                });
              });
              if (!modifiedItems || modifiedItems.length == 0) return;
              modifiedModules.current = modifiedItems;
              const deactivateModules = filter(modifiedItems, (item) => !item.isActive);
              if (deactivateModules && deactivateModules.length > 0) setShowDeactivateConfirmModal(true);
              else saveModules(modifiedItems);
            }}
          >
            {(formikProps: FormikProps<FormikState>) => {
              const { dirty, isValid, handleSubmit, handleReset } = formikProps;
              return (
                <Fragment>
                  <ProjectAdminSubContainerStyled mt={{ sm: '450' }} data-testid="module-data-container">
                    <Box my={{ sm: '550' }} mx={{ sm: '600' }} flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
                      <Box>
                        <Heading family="graphik" size="500" weight="600">
                          <Trans>Activate or Deactivate Project Modules</Trans>
                        </Heading>
                      </Box>
                      <Box
                        pt={{ sm: '500' }}
                        pb={{ sm: '600' }}
                        css={`
                          border-bottom: 1px solid ${({ theme }) => theme.colors.grey[300]};
                        `}
                      >
                        <Text family="graphik" size="300">
                          <Trans>Choose which modules you want to use on your project.</Trans>
                        </Text>
                      </Box>
                      {map(moduleExtList, (moduleItem, index) => {
                        return (
                          <ModuleAdminItem
                            key={moduleItem.id}
                            index={index}
                            moduleItem={moduleItem}
                            formikProps={formikProps}
                          />
                        );
                      })}
                    </Box>
                  </ProjectAdminSubContainerStyled>
                  {!moduleLoading && !moduleError && (
                    <FooterStyled>
                      <Box>
                        <Box mr={{ sm: '400' }}>
                          <FooterButtonStyled
                            data-testid="module-cancel"
                            disabled={updateProjectLoading || !dirty}
                            onClick={() => {
                              handleReset();
                            }}
                            appearance={ButtonAppearance.SECONDARY}
                          >
                            <Trans>Discard Changes</Trans>
                          </FooterButtonStyled>
                        </Box>
                        <FooterButtonStyled
                          data-testid="module-save"
                          onClick={() => {
                            handleSubmit();
                          }}
                          disabled={updateProjectLoading || !dirty || !isValid}
                          appearance={ButtonAppearance.SECONDARY}
                        >
                          {updateProjectLoading && <Spinner />}
                          {!updateProjectLoading && t`Save Changes`}
                        </FooterButtonStyled>
                      </Box>
                    </FooterStyled>
                  )}
                </Fragment>
              );
            }}
          </Formik>
        )}
      </ProjectAdminContainerStyled>
      {updateModuleError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <UpdateProjectModulesError
              updateModuleError={updateModuleError}
              close={() => {
                setUpdateModuleError(null);
              }}
            />
          </Suspense>
        </ErrorBoundary>
      )}
      {showDeactivateConfirmModal && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <DeactivateConfirmModal
              modules={filter(modifiedModules.current, (item) => !item.isActive)}
              onCancel={() => {
                setShowDeactivateConfirmModal(false);
              }}
              onConfirm={() => {
                setShowDeactivateConfirmModal(false);
                saveModules(modifiedModules.current);
              }}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </Fragment>
  );
};
